import { useCallback, useEffect, useRef, useState } from "react";

type AudioRecorderState = "idle" | "starting" | "recording" | "stopped";

type AudioRecorderError =
  | { kind: "permission_denied" }
  | { kind: "no_microphone" }
  | { kind: "unsupported" }
  | { kind: "internal"; message: string };

interface AudioRecorderResult {
  blob: Blob;
  mimeType: string;
  durationMs: number;
}

interface UseAudioRecorderOptions {
  maxDurationMs?: number;
  onAutoStop?: () => void;
  deviceId?: string | null;
}

interface UseAudioRecorderApi {
  state: AudioRecorderState;
  durationMs: number;
  result: AudioRecorderResult | null;
  error: AudioRecorderError | null;
  stream: MediaStream | null;
  start: () => Promise<void>;
  stop: () => void;
  reset: () => void;
}

const DEFAULT_MAX_MS = 60_000;

const MIME_CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4",
  "audio/mpeg",
];

const pickMimeType = (): string | null => {
  if (typeof MediaRecorder === "undefined") return null;
  for (const candidate of MIME_CANDIDATES) {
    if (MediaRecorder.isTypeSupported(candidate)) {
      return candidate;
    }
  }
  return null;
};

export const useAudioRecorder = (
  options: UseAudioRecorderOptions = {},
): UseAudioRecorderApi => {
  const { maxDurationMs = DEFAULT_MAX_MS, onAutoStop, deviceId } = options;
  const deviceIdRef = useRef<string | null>(deviceId ?? null);
  useEffect(() => {
    deviceIdRef.current = deviceId ?? null;
  }, [deviceId]);

  const [state, setState] = useState<AudioRecorderState>("idle");
  const [durationMs, setDurationMs] = useState(0);
  const [result, setResult] = useState<AudioRecorderResult | null>(null);
  const [error, setError] = useState<AudioRecorderError | null>(null);
  const [stream, setStream] = useState<MediaStream | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef<number>(0);
  const tickRef = useRef<number | null>(null);
  const autoStopRef = useRef<number | null>(null);
  const onAutoStopRef = useRef(onAutoStop);
  const unmountedRef = useRef(false);

  useEffect(() => {
    onAutoStopRef.current = onAutoStop;
  }, [onAutoStop]);

  const clearTimers = useCallback(() => {
    if (tickRef.current !== null) {
      window.clearInterval(tickRef.current);
      tickRef.current = null;
    }
    if (autoStopRef.current !== null) {
      window.clearTimeout(autoStopRef.current);
      autoStopRef.current = null;
    }
  }, []);

  const cleanup = useCallback(() => {
    clearTimers();
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      try {
        recorderRef.current.stop();
      } catch {
        // ignore — already stopped or torn down
      }
    }
    recorderRef.current = null;
    setStream((prev) => {
      prev?.getTracks().forEach((t) => t.stop());
      return null;
    });
  }, [clearTimers]);

  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      cleanup();
    };
  }, [cleanup]);

  const start = useCallback(async () => {
    setError(null);
    setResult(null);
    setDurationMs(0);
    // Sync flip so UI swaps before getUserMedia resolves.
    setState("starting");

    const mimeType = pickMimeType();
    if (!mimeType) {
      setError({ kind: "unsupported" });
      setState("idle");
      return;
    }

    let mediaStream: MediaStream;
    try {
      const audioConstraints: MediaTrackConstraints = deviceIdRef.current
        ? { deviceId: { exact: deviceIdRef.current } }
        : {};
      mediaStream = await navigator.mediaDevices.getUserMedia({
        audio:
          Object.keys(audioConstraints).length > 0 ? audioConstraints : true,
      });
    } catch (err) {
      if (unmountedRef.current) return;
      const name = (err as DOMException | undefined)?.name;
      if (name === "NotAllowedError" || name === "SecurityError") {
        setError({ kind: "permission_denied" });
      } else if (name === "NotFoundError" || name === "OverconstrainedError") {
        setError({ kind: "no_microphone" });
      } else {
        setError({
          kind: "internal",
          message: (err as Error).message ?? "Failed to start recording",
        });
      }
      setState("idle");
      return;
    }

    if (unmountedRef.current) {
      mediaStream.getTracks().forEach((t) => t.stop());
      return;
    }

    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(mediaStream, { mimeType });
    } catch (err) {
      mediaStream.getTracks().forEach((t) => t.stop());
      if (unmountedRef.current) return;
      setError({
        kind: "internal",
        message: (err as Error).message ?? "MediaRecorder construction failed",
      });
      setState("idle");
      return;
    }

    chunksRef.current = [];
    recorder.ondataavailable = (ev) => {
      if (ev.data && ev.data.size > 0) {
        chunksRef.current.push(ev.data);
      }
    };

    recorder.onstop = () => {
      const finalDuration = startedAtRef.current
        ? performance.now() - startedAtRef.current
        : 0;

      const blob = new Blob(chunksRef.current, { type: mimeType });
      mediaStream.getTracks().forEach((t) => t.stop());
      clearTimers();

      if (unmountedRef.current) return;

      setResult({
        blob,
        mimeType,
        durationMs: Math.round(finalDuration),
      });
      setDurationMs(Math.round(finalDuration));
      setStream(null);
      setState("stopped");
    };

    recorder.onerror = (event) => {
      console.error("[audio-recorder] MediaRecorder error", event);
      mediaStream.getTracks().forEach((t) => t.stop());
      clearTimers();
      recorderRef.current = null;
      if (unmountedRef.current) return;
      setStream(null);
      setState("idle");
      setError({
        kind: "internal",
        message: "Recording stopped unexpectedly. Try again.",
      });
    };

    recorderRef.current = recorder;
    setStream(mediaStream);
    startedAtRef.current = performance.now();
    setState("recording");
    recorder.start();

    tickRef.current = window.setInterval(() => {
      setDurationMs(Math.round(performance.now() - startedAtRef.current));
    }, 100);

    autoStopRef.current = window.setTimeout(() => {
      if (recorderRef.current && recorderRef.current.state === "recording") {
        recorderRef.current.stop();
      }
      onAutoStopRef.current?.();
    }, maxDurationMs);
  }, [maxDurationMs, clearTimers]);

  const stop = useCallback(() => {
    if (recorderRef.current && recorderRef.current.state === "recording") {
      recorderRef.current.stop();
    }
  }, []);

  const reset = useCallback(() => {
    cleanup();
    setState("idle");
    setDurationMs(0);
    setResult(null);
    setError(null);
  }, [cleanup]);

  return { state, durationMs, result, error, stream, start, stop, reset };
};
